import axios from "axios";
import { getToken, clearToken, wsUrlWithAuth } from "./authStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials=true so the browser attaches the HttpOnly auth cookie on
// every request. This is the primary auth transport (Bearer header below is
// belt-and-suspenders during backend transition and for pytest fixtures).
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// If the server rejects our credentials, purge the in-memory copy so the app
// resets cleanly. The HttpOnly cookie is server-managed — we cannot delete
// it from JS; the backend expires it or /auth/logout clears it.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const detail = err.response.data?.detail || "";
      if (/token|not authenticated/i.test(String(detail))) clearToken();
    }
    return Promise.reject(err);
  }
);

export function fmtErr(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

/**
 * @deprecated Legacy 2-arg form kept for any old caller. New code should call
 * `wsUrlWithAuth(path)` from `./authStore` directly.
 */
export function wsUrl(path, tokenOverride) {
  if (tokenOverride) {
    const base = BACKEND_URL.replace(/^http/i, "ws");
    const sep = path.includes("?") ? "&" : "?";
    return `${base}/api/ws${path}${sep}token=${encodeURIComponent(tokenOverride)}`;
  }
  return wsUrlWithAuth(path);
}

/**
 * Turn a user's avatar reference (relative `/api/files/...` or absolute URL)
 * into a fully-qualified src for <img>. Returns null when no avatar is set OR
 * when the caller is in Ghost mode without an opt-in reveal (in which case
 * public_profile() already filtered avatar_url server-side).
 *
 * The `bust` parameter lets callers force a fresh fetch after an upload
 * (append ?v={updated_at}) so stale <img> caches don't linger post-swap.
 */
export function avatarSrc(user, bust) {
  const raw = user?.avatar_url;
  if (!raw) return null;
  const url = /^https?:\/\//i.test(raw) ? raw : `${BACKEND_URL}${raw}`;
  const v = bust || user?.avatar_updated_at;
  return v ? `${url}?v=${encodeURIComponent(v)}` : url;
}

export default api;
